<template>
  <div
    v-if="currentPage || recentPages.length"
    class="relative shrink-0"
  >
    <button
      class="group flex h-8 max-w-56 min-w-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white py-1.5 pl-2 pr-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      @mouseenter="showPopover = true"
      @mouseleave="scheduleHide"
      @click="showPopover = !showPopover"
    >
      <component
        :is="displayIcon"
        v-if="displayIcon"
        class="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
      />
      <ClockIcon
        v-else
        class="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
      />
      <span class="truncate text-left">
        {{ displayPage ? displayPage.label : 'Recent pages' }}
      </span>
      <ChevronDownIcon class="ml-auto h-3 w-3 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>

    <!-- Popover -->
    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="showPopover && recentPages.length"
        class="absolute left-0 top-full z-50 mt-1 w-64 origin-top rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
      >
        <div class="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          Recent
        </div>
        <router-link
          v-for="page in recentPages"
          :key="`${page.routeName}-${page.params[Object.keys(page.params).find(k => k !== 'teamPid') || '']}`"
          :to="{ name: page.routeName, params: { ...page.params, teamPid: route.params.teamPid } }"
          class="flex items-center gap-x-2.5 px-2.5 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-slate-700/50"
          :class="isCurrentEntry(page)
            ? 'text-indigo-600 dark:text-primary-200'
            : 'text-gray-700 dark:text-gray-300'"
          @click="showPopover = false"
        >
          <component
            :is="getRecentPageIcon(page.routeName)"
            v-if="getRecentPageIcon(page.routeName)"
            class="h-4 w-4 shrink-0"
            :class="isCurrentEntry(page)
              ? 'text-indigo-500 dark:text-primary-300'
              : 'text-gray-400 dark:text-gray-500'"
          />
          <div class="min-w-0 flex-1">
            <div class="truncate font-medium">{{ page.label }}</div>
            <div class="text-[11px] text-gray-400 dark:text-gray-500">{{ page.typeLabel }}</div>
          </div>
        </router-link>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ClockIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import type { RecentPage } from '@/composables/useRecentPages'
import { getRecentPageIcon } from '@/composables/useRecentPages'

const route = useRoute()
const { recentPages, currentPage } = useRecentPages()

const showPopover = ref(false)
let hideTimeout: ReturnType<typeof setTimeout> | null = null

// Show the current page if we're on a tracked page, otherwise show most recent
const displayPage = computed(() => {
  return currentPage.value || recentPages.value[0] || null
})

const displayIcon = computed(() => {
  if (!displayPage.value) return null
  return getRecentPageIcon(displayPage.value.routeName)
})

const isCurrentEntry = (page: RecentPage) => {
  if (!currentPage.value) return false
  return (
    page.routeName === currentPage.value.routeName &&
    JSON.stringify(page.params) === JSON.stringify(currentPage.value.params)
  )
}

const scheduleHide = () => {
  hideTimeout = setTimeout(() => {
    showPopover.value = false
  }, 200)
}

const cancelHide = () => {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
}
</script>
